import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* ================= Block 1 — Hero with bento cards ================= */

const AboutHero = () => (
  <section data-testid="about-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="grid gap-12 lg:grid-cols-[500px_1fr] lg:gap-16">
        {/* Left — editorial text */}
        <div>
          <div className="flex items-center justify-center gap-3 sm:justify-start" aria-hidden="true">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 sm:hidden" />
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
              About WFII
            </span>
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))] sm:hidden" />
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 sm:hidden" />
          </div>
          <h1 className="font-serif-editorial mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
            Building Financial Confidence for the Digital Economy
          </h1>
          <p className="mt-8 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Digital finance is changing how businesses move money, manage
            records, and build trust.
          </p>
          <p className="mt-4 text-center text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-left">
            WFII makes stablecoins, digital assets, and modern payment rails
            clear and practical through education, research, and professional
            dialogue.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
            <Button
              asChild
              className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="about-hero-explore-programs-button"
            >
              <Link to="/programs">Explore Programs</Link>
            </Button>
            <Link
              to="/enrollment-request" state={{ type: "membership" }}
              className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
              data-testid="about-hero-become-member-link"
            >
              Become a Member
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                strokeWidth={1.75}
              />
            </Link>
          </div>
        </div>

        {/* Right — bento card mosaic (per design reference) */}
        <div className="grid gap-5 sm:grid-cols-2 lg:self-center">
          <div className="flex flex-col gap-5">
            {/* Card 1 — Education */}
            <div
              className="relative flex flex-1 flex-col overflow-hidden rounded-2xl bg-card/80 p-5 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-6"
              data-testid="about-card-education"
            >
              {/* Subtle teal chart illustration — sits in the empty lower area */}
              <svg
                className="pointer-events-none absolute inset-x-0 bottom-0 h-20 w-full text-[hsl(var(--accent))]"
                viewBox="0 0 400 80"
                preserveAspectRatio="none"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M0 76 C70 74, 120 64, 180 56 S300 38, 400 22 L400 80 L0 80 Z"
                  fill="currentColor"
                  fillOpacity="0.05"
                />
                <path
                  d="M0 76 C70 74, 120 64, 180 56 S300 38, 400 22"
                  stroke="currentColor"
                  strokeOpacity="0.22"
                  strokeWidth="1.5"
                />
                <path
                  d="M0 68 C80 66, 140 54, 210 44 S330 22, 400 8"
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeWidth="1.5"
                />
                <circle cx="180" cy="56" r="2.5" fill="currentColor" fillOpacity="0.28" />
                <circle cx="310" cy="35" r="2.5" fill="currentColor" fillOpacity="0.2" />
              </svg>
              <div className="relative">
                <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
                <h3 className="mt-5 text-lg font-semibold tracking-[-0.01em] text-foreground">
                  Education
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                  Programs, certificates, and webinars for finance professionals
                  and business teams.
                </p>
              </div>
            </div>
            {/* Card 3 — Professional Community (navy) */}
            <div
              className="wfii-stipple-dark relative flex flex-1 flex-col overflow-hidden rounded-2xl bg-primary p-5 text-primary-foreground shadow-[0_32px_64px_-24px_hsl(218_51%_15%/0.55)] sm:p-6"
              data-testid="about-card-community"
            >
              <div className="relative">
                <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
                <h3 className="mt-5 text-lg font-semibold tracking-[-0.01em]">
                  Professional Community
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-primary-foreground/75">
                  Members, partners, speakers,{" "}
                  <br className="hidden lg:block" />
                  roundtables, and working groups focused on responsible
                  adoption.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-5">
            {/* Card 2 — Research and Resources */}
            <div
              className="flex flex-col rounded-2xl bg-card/80 p-5 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-6"
              data-testid="about-card-research"
            >
              <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
              <h3 className="mt-5 text-lg font-semibold tracking-[-0.01em] text-foreground">
                Research and Resources
              </h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                Guides, checklists, briefings, and frameworks{" "}
                <br className="lg:hidden" />
                for practical
                digital finance understanding.
              </p>
            </div>
            {/* Card 4 — Rooted in Canada (photo card) */}
            <div
              className="relative flex min-h-[230px] flex-1 flex-col justify-end overflow-hidden rounded-2xl shadow-[0_32px_64px_-24px_hsl(218_51%_15%/0.55)] ring-1 ring-[hsl(var(--primary))]/10 lg:h-[304px] lg:min-h-0 lg:flex-none"
              data-testid="about-card-canada"
            >
              <img
                src="/toronto-skyline.jpg"
                alt="Toronto skyline at dusk"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-[hsl(218_51%_12%/0.88)] via-[hsl(218_51%_12%/0.35)] to-[hsl(218_51%_12%/0.12)]"
                aria-hidden="true"
              />
              <div className="relative p-5 text-primary-foreground sm:p-6">
                <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
                <h3 className="mt-4 text-lg font-semibold tracking-[-0.01em]">
                  Rooted in Canada
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-primary-foreground/80">
                  WFII brings a professional perspective{" "}
                  <br className="hidden lg:block" />
                  to digital finance education with global relevance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — Principles ================= */

const PRINCIPLES = [
  {
    n: "01",
    title: "Make Innovation Clear",
    body: "We explain how new financial tools work, where they fit, and what to consider before adoption.",
  },
  {
    n: "02",
    title: "Build Trust Through Practice",
    body: (<>Clear records, reliable processes, and understanding{" "}<br className="hidden lg:block" />shared build confidence.</>),
  },
  {
    n: "03",
    title: "Connect Learning to Real Work",
    body: "Education should support real workflows across payments, reporting, compliance, and governance.",
  },
  {
    n: "04",
    title: "Speak the Language of Business",
    body: "We connect digital assets to real business needs.",
  },
  {
    n: "05",
    title: "Prepare for What's Next",
    body: "Modern financial infrastructure is reshaping payments, treasury, records, and business finance.",
  },
];

const Principles = () => (
  <section data-testid="about-principles-section">
    <Container className="">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        What Guides Our Work
      </h2>
      <div className="mt-[41px] grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
        {PRINCIPLES.map((p) => (
          <div
            key={p.n}
            className="group border-t border-[hsl(var(--accent))] pt-6"
            data-testid={`about-principle-item-${p.n}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium tracking-[0.08em] text-muted-foreground">
                {p.n}
              </span>
              <span
                className="h-2 w-2 rounded-[2px] bg-[hsl(var(--accent))]/0 transition-colors group-hover:bg-[hsl(var(--accent))]/80"
                aria-hidden="true"
              />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground lg:text-[16px]">{p.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 3 — Who we serve ================= */

const TEAMS = [
  {
    title: "CFOs and Finance Leaders",
    body: "Understanding business impact, adoption questions, and internal readiness.",
    id: "cfos",
  },
  {
    title: "Accountants and Bookkeepers",
    body: "Working with records, reconciliation, documentation, and reporting.",
    id: "accountants",
  },
  {
    title: "Treasury and Payment Teams",
    body: (<>Managing payouts, settlement, liquidity,{" "}<br className="hidden lg:block" />and cross-border money movement.</>),
    id: "treasury",
  },
  {
    title: "Compliance and Risk Professionals",
    body: (<>Preparing for controls, policies, wallet risk,{" "}<br className="hidden lg:block" /><br className="lg:hidden" />and governance issues.</>),
    id: "compliance",
  },
  {
    title: "Business Owners and Founders",
    body: "Making informed decisions about digital finance tools and providers.",
    id: "founders",
  },
  {
    title: "Auditors & Assurance Professionals",
    body: (
      <>
        Evaluating financial records, transaction evidence, balances,
        valuation, controls,{" "}
        <br className="hidden lg:block" />
        and reporting.
      </>
    ),
    id: "auditors",
  },
];

const Teams = () => (
  <section data-testid="about-teams-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Built for the Teams Behind{" "}
          <br className="hidden lg:block" />
          Business Finance
        </h2>
        <p className="mx-auto mt-[21px] max-w-[64ch] text-base leading-relaxed text-muted-foreground lg:mt-[26px] lg:max-w-none">
          WFII supports the professionals and organizations working where
          financial innovation becomes real:{" "}
          <br className="hidden lg:block" />
          payments, records, reporting, compliance, treasury, and business
          decisions.
        </p>
      </div>
      <div className="mt-[41px] grid gap-6 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3 lg:gap-8">
        {TEAMS.map((t) => (
          <div
            key={t.id}
            className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-8"
            data-testid={`about-team-card-${t.id}`}
          >
            <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <h3 className="mt-6 text-lg font-semibold tracking-[-0.01em] text-foreground">
              {t.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground lg:text-[16px]">{t.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 4 — Final CTA ================= */

const AboutFinalCta = () => (
  <section data-testid="about-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Stay Ready for What Comes Next
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              WFII helps professionals and organizations understand modern
              financial infrastructure with clarity, responsibility, and
              confidence.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="about-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function AboutPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <AboutHero />
          <Principles />
          <Teams />
          <AboutFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
